/**
 * Componente: Login Screen Rediseñado
 * Diseño: Gradiente Turquesa-Púrpura Premium
 * Ubicación: src/pages/Login.jsx
 *
 * Login en pasos. La contraseña sola YA NO da acceso: el 2FA (código de una
 * app autenticadora) es obligatorio para todo usuario de municipio.
 *  1. Email + contraseña (POST /api/auth/login). El backend responde una de:
 *     - `token` (JWT real): entra directo. Solo pasa si este navegador manda un
 *       dispositivo confiable válido de ESTE usuario ("recordar este
 *       dispositivo", ver utils/dispositivoConfiable.js).
 *     - `requiere_2fa`: ya tiene 2FA → paso "código" (<Login2faCodigo>).
 *     - `requiere_configurar_2fa`: todavía no lo tiene → paso "configurar"
 *       (<Login2faConfigurar>: QR + código), parte del login, no opcional.
 *  2. En ambos pasos el backend entrega recién ahí el JWT real.
 * Ver components/Login2fa.jsx.
 */

import React, { useState } from 'react';
import axios from 'axios';
import {
  STROKE_DEGRADE,
  IconEmail,
  IconContrasena,
  IconMostrarContrasena,
  IconOcultarContrasena,
} from '../components/icons';
import { Login2faCodigo, Login2faConfigurar } from '../components/Login2fa';
import { CLAVE_MENSAJE_ACCESO } from '../utils/configurarAxios';
import {
  obtenerDispositivoConfiable,
  guardarDispositivoConfiable,
  borrarDispositivoConfiable,
} from '../utils/dispositivoConfiable';
import '../styles/login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [contraseña, setContraseña] = useState('');
  const [mostrarContraseña, setMostrarContraseña] = useState(false);
  const [recordarUsuario, setRecordarUsuario] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  // Paso del 2FA: token temporal que devolvió el login (null = todavía en
  // email/contraseña) y cuál de los dos pasos toca ('codigo' | 'configurar').
  const [token2fa, setToken2fa] = useState(null);
  const [modo2fa, setModo2fa] = useState(null);

  // Cargar datos guardados al montar
  React.useEffect(() => {
    const emailGuardado = localStorage.getItem('email_recordado');
    if (emailGuardado) {
      setEmail(emailGuardado);
      setRecordarUsuario(true);
    }

    // Si el interceptor de axios nos trajo acá porque el municipio quedó
    // suspendido o dado de baja con la sesión abierta (configurarAxios.js),
    // deja el motivo para mostrarlo en el cartel rojo. Se lee una sola vez.
    try {
      const mensajeAcceso = sessionStorage.getItem(CLAVE_MENSAJE_ACCESO);
      if (mensajeAcceso) {
        sessionStorage.removeItem(CLAVE_MENSAJE_ACCESO);
        setError(mensajeAcceso);
      }
    } catch (e) {
      // sessionStorage no disponible: el login arranca vacío, como siempre.
    }
  }, []);

  // Vuelve del paso de 2FA al formulario de email + contraseña, descartando el
  // token temporal. La contraseña se limpia (no tiene por qué quedar en
  // memoria); el email queda para no retipearlo.
  const volverAlLogin = (mensaje) => {
    setToken2fa(null);
    setModo2fa(null);
    setContraseña('');
    setError(mensaje || '');
  };

  // Todo lo que pasa cuando el backend ya entregó el JWT real: lo usan el
  // login directo (dispositivo confiable) y los dos pasos de 2FA.
  const completarLogin = (data, recordarDispositivo) => {
    // Token y usuario en sessionStorage (no localStorage) a propósito:
    // la sesión de un municipio se borra sola al cerrar la pestaña o el
    // navegador — no queda una sesión abierta indefinidamente en una PC
    // compartida de la oficina. "Recordar este usuario" abajo es un caso
    // aparte: solo evita re-tipear el email, nunca guarda el token, así
    // que sigue en localStorage (tiene que sobrevivir el cierre del
    // navegador para que sirva de algo). El dispositivo confiable
    // (utils/dispositivoConfiable.js) también va en localStorage, pero NO es
    // una sesión: sin la contraseña del usuario no sirve de nada.
    sessionStorage.setItem('token', data.token);

    // Guardar datos del usuario (nombre, rol, municipio)
    sessionStorage.setItem('usuario', JSON.stringify(data.usuario));

    // Dispositivo confiable nuevo, si se pidió recordarlo: asociado al email.
    if (recordarDispositivo && data.dispositivo_token) {
      guardarDispositivoConfiable(email, data.dispositivo_token, data.dispositivo_expira);
    }

    // Recordar usuario si está marcado
    if (recordarUsuario) {
      localStorage.setItem('email_recordado', email);
    } else {
      localStorage.removeItem('email_recordado');
    }

    // Redirigir al dashboard
    window.location.href = '/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError('');

    try {
      // Validar datos
      if (!email || !contraseña) {
        setError('Por favor completa email y contraseña');
        setCargando(false);
        return;
      }

      // Si este navegador ya recordó un dispositivo para ESTE email, se manda:
      // si sigue vigente, el backend entra directo sin pedir el código.
      const dispositivoToken = obtenerDispositivoConfiable(email);

      // Llamar al backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          email,
          contraseña,
          ...(dispositivoToken ? { dispositivo_token: dispositivoToken } : {}),
        }
      );
      const data = response.data;

      // El backend no honró el dispositivo (venció o dejó de valer): se borra
      // para no volver a mandarlo.
      if (data.dispositivo_token_invalido) {
        borrarDispositivoConfiable(email);
      }

      if (data.requiere_2fa || data.requiere_configurar_2fa) {
        // Todavía sin acceso: solo hay un token temporal. Se pasa al paso que
        // corresponda, sin guardar nada en el navegador.
        setModo2fa(data.requiere_2fa ? 'codigo' : 'configurar');
        setToken2fa(data.token_2fa);
        return;
      }

      if (!data.token) {
        setError('Respuesta inesperada del servidor. Probá de nuevo.');
        return;
      }
      completarLogin(data, false);
    } catch (err) {
      setError(err.response?.data?.mensaje || 'Error al ingresar. Verifica tus datos.');
      console.error('Error de login:', err);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="login-container">
      {/* Gradiente de fondo (turquesa -> púrpura) */}
      <div className="login-gradient"></div>

      <div className="login-card">
        {/* Logo Betech */}
        <div className="login-logo">
          <img src="/logo-betech.svg" alt="Betech" className="logo-img" />
        </div>

        {token2fa && modo2fa === 'codigo' && (
          <Login2faCodigo token2fa={token2fa} onCompleto={completarLogin} onVolver={volverAlLogin} />
        )}
        {token2fa && modo2fa === 'configurar' && (
          <Login2faConfigurar token2fa={token2fa} onCompleto={completarLogin} onVolver={volverAlLogin} />
        )}

        {!token2fa && (<>
        {/* Título */}
        <h1 className="login-title">
          Sistema de Gestión de Licencias de Conducir
        </h1>
        <p className="login-subtitle">Ingresa tus credenciales para continuar</p>

        {/* Formulario */}
        <form key="form-login" onSubmit={handleSubmit} className="login-form">
          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <span className="input-icon"><IconEmail size={16} stroke={STROKE_DEGRADE} /></span>
              <input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                disabled={cargando}
                required
              />
            </div>
          </div>

          {/* Contraseña */}
          <div className="form-group">
            <label htmlFor="contraseña">Contraseña</label>
            <div className="input-wrapper">
              <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
              <input
                id="contraseña"
                type={mostrarContraseña ? 'text' : 'password'}
                placeholder="••••••••"
                value={contraseña}
                onChange={(e) => setContraseña(e.target.value)}
                className="form-input"
                disabled={cargando}
                required
              />
              <button
                type="button"
                className="btn-mostrar-contraseña"
                onClick={() => setMostrarContraseña(!mostrarContraseña)}
                tabIndex="-1"
              >
                {mostrarContraseña
                  ? <IconOcultarContrasena size={16} stroke="#555" />
                  : <IconMostrarContrasena size={16} stroke="#555" />}
              </button>
            </div>
          </div>

          {/* Recordar usuario */}
          <div className="form-remember">
            <input
              id="recordar"
              type="checkbox"
              checked={recordarUsuario}
              onChange={(e) => setRecordarUsuario(e.target.checked)}
              disabled={cargando}
            />
            <label htmlFor="recordar">Recordar este usuario</label>
          </div>

          {/* Mensaje de error */}
          {error && <div className="error-message">{error}</div>}

          {/* Botón de ingreso */}
          <button
            type="submit"
            className="btn-ingresar"
            disabled={cargando}
          >
            {cargando ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>

        {/* Links de ayuda */}
        <div className="login-links">
          <a href="/recuperar-contraseña" className="link-ayuda">
            ¿Olvidaste tu contraseña?
          </a>
        </div>
        </>)}

        {/* Footer */}
        <div className="login-footer">
          <p>
            © 2026 Betech Solutions |
            <a href="https://be-tech.com.ar" target="_blank" rel="noopener noreferrer">
              {' '}www.be-tech.com.ar
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
